import 'reflect-metadata';
import './src/utils/branch/branch';
import { LoadingScreen } from '@Components/Common/LoadingScreen';
import { ScreenSizeSpy } from '@Components/ScreenSize/ScreenSizeSpy';
import { Navigator2 } from '@Navigator2/Navigator2';
import { Assets, ImportedAssets } from '@Utils/AssetsStore';
import { utilStyles } from '@Utils/utilStyles';
import { useDimensions } from '@react-native-community/hooks';
import { useFonts } from 'expo-font';
import { initialiseOtaManager } from 'expo-ota-manager';
import { StatusBar } from 'expo-status-bar';
import { observer } from 'mobx-react-lite';
import React, { Suspense, useEffect } from 'react';
import { LogBox, Platform } from 'react-native';
import { DefaultTheme, Provider as PaperProvider } from 'react-native-paper';
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context';
import styled from 'styled-components/native';

import { useAppPresenter } from './AppPresenter';
import { RootErrorBoundary } from './src/RootErrorBoundary';
import OwnableTheme from './src/assets/OwnableTheme';

import { useAnalyticsStore } from '~stores/analytics';

setTimeout(() => {
  initialiseOtaManager({});
}, 5000);

const StyledContainer = styled.KeyboardAvoidingView`
  flex: 1;
  display: flex;
  align-items: stretch;
  flex-direction: column;
`;

const StyledStatusBarContainer = styled.View`
  background-color: white;
`;

const theme = {
  ...DefaultTheme,
  dark: false,
};

if (__DEV__) {
  // Change this boolean to false if you want to display error logs in the app
  // Use case for disabling it includes working with elements located near the bottom of the screen
  // The popups that sometimes show up can be frustrating to constantly dismiss
  const disableAllLogs = true;
  if (Platform.OS !== 'web') {
    LogBox.ignoreAllLogs(disableAllLogs);
  }
}

export default observer(() => {
  useAnalyticsStore(); // we just initialize the store

  const { initApp, isReady } = useAppPresenter();

  const { window } = useDimensions();

  // noinspection JSDeprecatedSymbols
  const [fontsLoaded] = useFonts({
    bold: ImportedAssets[Assets.Font_Gilroy_Bold],
    light: ImportedAssets[Assets.Font_Gilroy_Light],
    regular: ImportedAssets[Assets.Font_Gilroy_Medium],
    semiBold: ImportedAssets[Assets.Font_Gilroy_SemiBold],
    black: ImportedAssets[Assets.Font_Gilroy_Black],
  });

  useEffect(() => {
    initApp().then();
  }, []);

  if (!fontsLoaded || !isReady) {
    return <LoadingScreen />;
  }

  return (
    <PaperProvider theme={theme}>
      <ScreenSizeSpy />
      <SafeAreaProvider>
        <StyledContainer
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <SafeAreaView
            edges={['right', 'top', 'left']}
            style={[
              {
                backgroundColor: OwnableTheme.colors.white,
              },
              Platform.OS === 'web'
                ? { height: window.height }
                : utilStyles.flexOne,
            ]}
          >
            {Platform.OS !== 'web' && (
              <StyledStatusBarContainer>
                <StatusBar style='light' />
              </StyledStatusBarContainer>
            )}
            <Suspense fallback={<></>}>
              <RootErrorBoundary>
                <Navigator2 />
              </RootErrorBoundary>
            </Suspense>
          </SafeAreaView>
        </StyledContainer>
      </SafeAreaProvider>
    </PaperProvider>
  );
});
